// Role-aware operational data hooks. Most build Firestore queries within the
// caller's Rules scope; Contacts and Actions use server-authoritative
// callables where direct collection reads cannot express the required policy.

import { useEffect, useMemo, useState } from 'react';
import { where, orderBy, type QueryConstraint } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCollection } from './useCollection';
import { functions } from '../firebase';
import { ACTIONS_CHANGED_EVENT } from '../lib/actions';
import { createRefreshController, toError } from '../lib/silentRefresh';
import { hasCouncilFullRead } from '../lib/visibility';
import { tenantWhere, useActiveTenantId } from '../lib/tenant';
import type {
  Action,
  AffiliatedHolder,
  Contact,
  DirectoryPerson,
  AssignablePerson,
  PersonOption,
  EventTemplate,
  Goal,
  Initiative,
  QualificationArea,
  QualificationAssignment,
  StrategicPriority,
  Task,
  UserProfile,
} from '../types';

/** Strategic priorities — readable by every provisioned user, within their
 *  own tenant (Phase 5B — every hook below carries the same tenantWhere()
 *  boundary, in addition to whatever district/council scoping it already
 *  had; see src/lib/tenant.ts). */
export function useVisiblePriorities() {
  return useCollection<StrategicPriority>('strategicPriorities', [
    orderBy('programYear', 'desc'),
    tenantWhere(),
  ]);
}

/** Goals visible to this user — council-level strategy, so every provisioned
 *  user sees every goal regardless of district (unlike Tasks/Initiatives,
 *  which stay district-scoped below). */
export function useVisibleGoals(_profile: UserProfile) {
  return useCollection<Goal>('goals', [tenantWhere()]);
}

/** Qualifications is council-level like Goals — every provisioned user
 *  reads every area/assignment in the tenant, regardless of district;
 *  write access is gated separately by canManageQualifications() (admin
 *  only for now — see lib/visibility.ts). Both collections are small
 *  (a handful of areas, one doc per role/qualification grant), so — same
 *  as Goals — this is a single unscoped read, not a per-area query. */
export function useVisibleQualificationAreas(_profile: UserProfile) {
  return useCollection<QualificationArea>('qualificationAreas', [tenantWhere()]);
}

export function useVisibleQualificationAssignments(_profile: UserProfile) {
  return useCollection<QualificationAssignment>('qualificationAssignments', [tenantWhere()]);
}

/** Tasks visible to this user — anyone in the district sees every task in
 *  it, regardless of committee or assignment (editing stays gated
 *  separately, by position hierarchy or direct assignment). */
export function useVisibleTasks(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<Task>('tasks', constraints, enabled);
}

/** Event templates — the shared backwards-planning library, readable by every
 *  provisioned user. Filter to `active` in the adopt picker. */
export function useEventTemplates() {
  return useCollection<EventTemplate>('eventTemplates', [orderBy('name', 'asc'), tenantWhere()]);
}

/** Initiatives visible to this user — anyone in the district sees its
 *  initiatives (rules allow read to any profile in the district). */
export function useVisibleInitiatives(profile: UserProfile) {
  const admin = profile.role === 'admin';
  const unscoped = admin || hasCouncilFullRead(profile);
  const activeTenantId = useActiveTenantId();
  const constraints: QueryConstraint[] = useMemo(
    () => [tenantWhere(), ...(unscoped || !profile.districtId ? [] : [where('districtId', '==', profile.districtId)])],
    [unscoped, profile.districtId, activeTenantId],
  );
  const enabled = unscoped || !!profile.districtId;
  return useCollection<Initiative>('initiatives', constraints, enabled);
}

/** Actions are delivered by the server-authoritative V2 allowlist. Direct
 * browser reads are closed in Rules; the callable evaluates tenant,
 * district, or trusted Subcommittee `actions.view`, plus owner/participant
 * relationships. */
export function useVisibleActions(profile: UserProfile) {
  const activeTenantId = useActiveTenantId();
  // `refreshError` is a failed background refresh: the data above it is still the last good answer.
  const [state, setState] = useState<{ data: Action[]; manualActionSubcommitteeIds: string[]; loading: boolean; error: Error | null; refreshError: Error | null }>({
    data: [], manualActionSubcommitteeIds: [], loading: true, error: null, refreshError: null,
  });
  const identityKey = [
    profile.id, profile.role, profile.districtId ?? '', profile.contactId ?? '',
    (profile.positionIds ?? []).join(','), String(profile.councilAccess ?? ''), activeTenantId,
  ].join('|');

  useEffect(() => {
    // Clear-first only when the identity/council/authorization context changes (this effect re-runs);
    // focus and change events refresh silently, keeping the current data on screen.
    const controller = createRefreshController({
      fetch: () => httpsCallable<Record<string, never>, { actions: Action[]; manualActionSubcommitteeIds: string[] }>(functions, 'listVisibleActionsV2')({}).then((r) => r.data),
      onStart: () => setState({ data: [], manualActionSubcommitteeIds: [], loading: true, error: null, refreshError: null }),
      onResult: (d) => setState({ data: d.actions, manualActionSubcommitteeIds: d.manualActionSubcommitteeIds ?? [], loading: false, error: null, refreshError: null }),
      onError: (error, background) => {
        const err = toError(error, 'Actions could not be loaded.');
        if (background) {
          console.warn('Background refresh of Actions failed; keeping the data on screen.', err);
          setState((s) => ({ ...s, refreshError: err }));
        } else {
          setState({ data: [], manualActionSubcommitteeIds: [], loading: false, error: err, refreshError: null });
        }
      },
    });
    const onChanged = () => controller.refresh('event');
    const onFocus = () => controller.refresh('focus');
    controller.start();
    window.addEventListener(ACTIONS_CHANGED_EVENT, onChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      controller.dispose();
      window.removeEventListener(ACTIONS_CHANGED_EVENT, onChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, [identityKey]);

  return state;
}

/** Raised after a successful local Contact mutation so callable-backed
 * Contact views refresh without restoring a full-document listener. */
export const CONTACTS_CHANGED_EVENT = 'compass:contacts-changed';

/** Contacts (District Roster) — delivered through the V2 server allowlist.
 * Firestore Rules deny every browser read of the underlying full documents. */
export function useVisibleContacts(profile: UserProfile | null) {
  const activeTenantId = useActiveTenantId();
  // `refreshError` is a failed background refresh: the data above it is still the last good answer.
  const [state, setState] = useState<{ data: Contact[]; loading: boolean; error: Error | null; refreshError: Error | null }>({
    data: [],
    loading: !!profile,
    error: null,
    refreshError: null,
  });
  const identityKey = [
    profile?.id ?? '',
    profile?.role ?? '',
    profile?.districtId ?? '',
    (profile?.positionIds ?? []).join(','),
    String(profile?.councilAccess ?? ''),
    String(profile?.contactAccess ?? ''),
    activeTenantId,
  ].join('|');

  useEffect(() => {
    if (!profile) {
      setState({ data: [], loading: false, error: null, refreshError: null });
      return;
    }
    // Drop the prior allowlist immediately when the identity/council/authorization context changes (this effect
    // re-runs): a tenant/Position downgrade must never leave formerly-Full Contact objects resident in browser
    // state while the new authoritative response is in flight. Focus and change events refresh silently.
    const controller = createRefreshController({
      fetch: () => httpsCallable<Record<string, never>, { contacts: Contact[] }>(functions, 'listVisibleContactsV2')({}).then((r) => r.data.contacts),
      onStart: () => setState({ data: [], loading: true, error: null, refreshError: null }),
      onResult: (contacts) => setState({ data: contacts, loading: false, error: null, refreshError: null }),
      onError: (error, background) => {
        const err = toError(error, 'Contacts could not be loaded.');
        if (background) {
          console.warn('Background refresh of Contacts failed; keeping the data on screen.', err);
          setState((s) => ({ ...s, refreshError: err }));
        } else {
          setState({ data: [], loading: false, error: err, refreshError: null });
        }
      },
    });
    const onChanged = () => controller.refresh('event');
    const onFocus = () => controller.refresh('focus');
    controller.start();
    window.addEventListener(CONTACTS_CHANGED_EVENT, onChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      controller.dispose();
      window.removeEventListener(CONTACTS_CHANGED_EVENT, onChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, [identityKey]);

  return state;
}


/** Contacts directory — every Person, delivered through the server allowlist
 * (listDirectoryV1) so Basic viewers never receive Full-only fields. */
export function useDirectory(profile: UserProfile | null) {
  const activeTenantId = useActiveTenantId();
  const [state, setState] = useState<{ data: DirectoryPerson[]; loading: boolean; error: Error | null; districtAffiliationsEnabled: boolean }>({
    data: [],
    loading: !!profile,
    error: null,
    districtAffiliationsEnabled: false,
  });
  const identityKey = [
    profile?.id ?? '', profile?.role ?? '', profile?.districtId ?? '',
    (profile?.positionIds ?? []).join(','), String(profile?.councilAccess ?? ''), String(profile?.contactAccess ?? ''),
    activeTenantId,
  ].join('|');

  useEffect(() => {
    if (!profile) {
      setState({ data: [], loading: false, error: null, districtAffiliationsEnabled: false });
      return;
    }
    let cancelled = false;
    const load = () => {
      const call = httpsCallable<Record<string, never>, { people: DirectoryPerson[]; districtAffiliationsEnabled?: boolean }>(functions, 'listDirectoryV1');
      void call({})
        .then((result) => { if (!cancelled) setState({ data: result.data.people, loading: false, error: null, districtAffiliationsEnabled: result.data.districtAffiliationsEnabled === true }); })
        .catch((error: unknown) => {
          if (!cancelled) setState({ data: [], loading: false, error: error instanceof Error ? error : new Error('Contacts could not be loaded.'), districtAffiliationsEnabled: false });
        });
    };
    // A downgrade must never leave formerly-Full people resident while the new response is in flight.
    setState({ data: [], loading: true, error: null, districtAffiliationsEnabled: false });
    load();
    window.addEventListener(CONTACTS_CHANGED_EVENT, load);
    return () => { cancelled = true; window.removeEventListener(CONTACTS_CHANGED_EVENT, load); };
  }, [identityKey]);

  return state;
}


/** People who hold a district seat from outside that district (listAffiliatedHoldersV1), for the Org Chart, the Dashboard seat count
 *  and committee health. Those screens are built from roster Contacts, and an outside-district holder deliberately has none.
 *  `enabled` is whether this council has made District Affiliations available. If the feed cannot be read (an older server, a
 *  network error) it fails to "nothing extra": every screen then shows exactly what it showed before the feature existed. */
export function useAffiliatedHolders(profile: UserProfile | null) {
  const activeTenantId = useActiveTenantId();
  // `refreshError` is a failed background refresh: the holders above it are still the last good answer.
  const [state, setState] = useState<{ holders: AffiliatedHolder[]; enabled: boolean; loading: boolean; refreshError: Error | null }>({ holders: [], enabled: false, loading: !!profile, refreshError: null });
  const identityKey = [
    profile?.id ?? '', profile?.role ?? '', profile?.districtId ?? '',
    (profile?.positionIds ?? []).join(','), String(profile?.councilAccess ?? ''), String(profile?.contactAccess ?? ''),
    activeTenantId,
  ].join('|');

  useEffect(() => {
    if (!profile) {
      setState({ holders: [], enabled: false, loading: false, refreshError: null });
      return;
    }
    // A scope change (this effect re-running) must never leave the previous viewer's holders resident while the new
    // response is in flight. Focus and change events refresh silently, keeping the current holders on screen.
    const controller = createRefreshController({
      fetch: () => httpsCallable<Record<string, never>, { enabled?: boolean; holders?: AffiliatedHolder[] }>(functions, 'listAffiliatedHoldersV1')({}).then((r) => r.data),
      onStart: () => setState({ holders: [], enabled: false, loading: true, refreshError: null }),
      onResult: (d) => setState({ holders: d.enabled === true && Array.isArray(d.holders) ? d.holders : [], enabled: d.enabled === true, loading: false, refreshError: null }),
      onError: (error, background) => {
        if (background) {
          const err = toError(error, 'Affiliated holders could not be refreshed.');
          console.warn('Background refresh of affiliated holders failed; keeping the data on screen.', err);
          setState((s) => ({ ...s, refreshError: err }));
        } else {
          setState({ holders: [], enabled: false, loading: false, refreshError: null }); // fails to "nothing extra"
        }
      },
    });
    const onChanged = () => controller.refresh('event');
    const onFocus = () => controller.refresh('focus');
    controller.start();
    window.addEventListener(CONTACTS_CHANGED_EVENT, onChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      controller.dispose();
      window.removeEventListener(CONTACTS_CHANGED_EVENT, onChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, [identityKey]);

  return state;
}


// ---- Phase 2.3: People as the working identity --------------------------------------------------------------

export interface AssignablePeopleState {
  /** One option per (Person, district) so district-scoped pickers can filter by the record's district. */
  options: PersonOption[];
  /** Every selectable Person once, by id. */
  byId: Map<string, AssignablePerson>;
  /** 'contacts-only' (Stage 1) lists only People who are already on the roster. */
  stage: 'contacts-only' | 'all-people';
  loading: boolean;
  error: Error | null;
}

/** One shared, short-lived copy of the picker list per (tenant, viewer): every page that needs it reuses it, instead of
 *  each re-fetching the whole district list. Cleared whenever a person or roster record changes. */
const PEOPLE_TTL_MS = 5 * 60 * 1000;
const peopleCache = new Map<string, { at: number; promise: Promise<{ stage: 'contacts-only' | 'all-people'; people: AssignablePerson[] }> }>();
function loadAssignablePeople(key: string) {
  const hit = peopleCache.get(key);
  if (hit && Date.now() - hit.at < PEOPLE_TTL_MS) return hit.promise;
  const call = httpsCallable<Record<string, never>, { stage: 'contacts-only' | 'all-people'; people: AssignablePerson[] }>(functions, 'listAssignablePeopleV1');
  const promise = call({}).then((r) => r.data);
  peopleCache.set(key, { at: Date.now(), promise });
  promise.catch(() => peopleCache.delete(key));
  return promise;
}

/** People the viewer may assign to something (names, PER refs and districts only — no contact details). */
export function useAssignablePeople(profile: UserProfile | null): AssignablePeopleState {
  const activeTenantId = useActiveTenantId();
  const [state, setState] = useState<AssignablePeopleState>({ options: [], byId: new Map(), stage: 'contacts-only', loading: !!profile, error: null });
  const identityKey = [profile?.id ?? '', profile?.role ?? '', profile?.districtId ?? '', String(profile?.councilAccess ?? ''), activeTenantId].join('|');

  useEffect(() => {
    if (!profile) { setState({ options: [], byId: new Map(), stage: 'contacts-only', loading: false, error: null }); return; }
    let cancelled = false;
    const load = () => {
      void loadAssignablePeople(identityKey)
        .then((data) => {
          if (cancelled) return;
          const options: PersonOption[] = [];
          for (const p of data.people) {
            const districts = p.districtIds.length ? p.districtIds : [''];
            for (const districtId of districts) options.push({ id: p.personId, name: p.name, ref: p.ref, districtId, provisional: p.provisional, commissionerEligible: p.commissionerEligible });
          }
          setState({ options, byId: new Map(data.people.map((p) => [p.personId, p])), stage: data.stage, loading: false, error: null });
        })
        .catch((error: unknown) => {
          if (!cancelled) setState((prev) => ({ ...prev, loading: false, error: error instanceof Error ? error : new Error('People could not be loaded.') }));
        });
    };
    load();
    const refresh = () => { peopleCache.clear(); load(); };
    window.addEventListener(CONTACTS_CHANGED_EVENT, refresh);
    return () => { cancelled = true; window.removeEventListener(CONTACTS_CHANGED_EVENT, refresh); };
  }, [identityKey]);

  return state;
}

/**
 * Display names for People referenced by records the viewer can read, including People outside the viewer's own
 * scope (the picker list would not contain them). Name + PER ref only. Cached per tenant for the session.
 */
const labelCache = new Map<string, { name: string; ref: string | null } | null>();
export function usePersonLabels(personIds: Array<string | null | undefined>, known?: Map<string, AssignablePerson>) {
  const activeTenantId = useActiveTenantId();
  const [, bump] = useState(0);
  const wanted = [...new Set(personIds.filter((x): x is string => typeof x === 'string' && x.length > 0))]
    .filter((id) => !known?.has(id) && !labelCache.has(`${activeTenantId}:${id}`));
  const wantedKey = wanted.sort().join(',');
  useEffect(() => {
    if (!wantedKey) return;
    let cancelled = false;
    const ids = wantedKey.split(',');
    const call = httpsCallable<{ personIds: string[] }, { labels: Record<string, { personId: string; name: string; ref: string | null } | null> }>(functions, 'getPersonLabelsV1');
    void (async () => {
      for (let i = 0; i < ids.length; i += 100) {
        try {
          const res = await call({ personIds: ids.slice(i, i + 100) });
          for (const [id, label] of Object.entries(res.data.labels)) labelCache.set(`${activeTenantId}:${id}`, label ? { name: label.name, ref: label.ref } : null);
        } catch {
          for (const id of ids.slice(i, i + 100)) labelCache.set(`${activeTenantId}:${id}`, null);
        }
      }
      if (!cancelled) bump((n) => n + 1);
    })();
    return () => { cancelled = true; };
  }, [wantedKey, activeTenantId]);
  /** Name for a Person id: the picker list first, then the label cache. */
  return (personId: string | null | undefined): string | null => {
    if (!personId) return null;
    const hit = known?.get(personId);
    if (hit) return hit.name;
    return labelCache.get(`${activeTenantId}:${personId}`)?.name ?? null;
  };
}
